import kebabCase from 'lodash/kebabCase';
import { ref } from 'vue';
import State from '@/state/State';
import { captureCharts, captureKpis } from '@/utils/pdfChartCapture';
import { generateSubsectionPdf } from '@/utils/pdfSubsectionSheet';

export default function useSubsectionDownload() {
  const isGenerating = ref(false);

  function formatDate(date) {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function formatDateSlug(date) {
    return date.toISOString().slice(0, 10);
  }

  function getEmployerName() {
    return State.state.company?.name || 'Employer';
  }

  function getEmployerSlug() {
    return kebabCase(getEmployerName());
  }

  /**
   * Canonical shape for additional sheet data. Children that contribute PDF data
   * should `defineExpose({ getPdfData: () => SheetExtras })` and parents wiring
   * them up via `<analytics-sub-page :get-sheet-config="...">` should pass that
   * function through unchanged. Adopters MUST NOT reshape between child and
   * composable.
   *
   * @typedef {Object} SheetExtras
   * @property {string} [label]               Override the descriptor's default label.
   * @property {string} [period]              Period text for the footer; defaults to the generated date.
   * @property {boolean} [isCompare]          Selects the compare-sheet layout; disables KPI capture.
   * @property {Object} [compareData]         `{ metric, divisions, period, generatedDate }` consumed by the compare layout.
   * @property {Array} [topKpis]              Pre-computed KPIs for the top strip (overrides DOM-captured KPIs).
   * @property {Array} [trendingBrands]       Trending Brands card data (Rewards pages).
   * @property {Array} [charts]               Pre-captured charts; when supplied, `captureCharts` is skipped.
   */

  /**
   * @typedef {Object} SheetDescriptor
   * @property {string} label                       Default section title shown in the header.
   * @property {() => HTMLElement} getContainerEl   Resolves the DOM element scraped for `data-pdf-*` markers.
   * @property {boolean} [inlineKpis=false]         Disables the top KPI strip; KPIs render next to their charts instead.
   * @property {() => SheetExtras} [getPdfData]     Resolves additional sheet data at download time.
   */

  function buildSheet(descriptor, employer, generatedDate) {
    const extras = descriptor.getPdfData?.() || {};
    const containerEl = descriptor.getContainerEl();
    const inlineKpis = descriptor.inlineKpis || false;

    const charts = extras.charts || captureCharts(containerEl);
    const kpis = extras.isCompare || inlineKpis || extras.topKpis ? [] : captureKpis(containerEl);

    return {
      label: extras.label || descriptor.label,
      employer,
      period: extras.period || generatedDate,
      generatedDate,
      kpis,
      charts,
      isCompare: extras.isCompare || false,
      compareData: extras.compareData || null,
      inlineKpis,
      topKpis: extras.topKpis || null,
      trendingBrands: extras.trendingBrands || null,
    };
  }

  /**
   * Download one or more subsection sheets as a single PDF.
   *
   * @param {SheetDescriptor[]} descriptors
   * @returns {Promise<{ success: boolean, error?: Error }>}
   */
  async function downloadSheets(descriptors) {
    if (isGenerating.value) {
      return { success: false };
    }
    isGenerating.value = true;

    try {
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const now = new Date();
      const employer = getEmployerName();
      const generatedDate = formatDate(now);

      const sheets = [];
      for (const descriptor of descriptors) {
        sheets.push(buildSheet(descriptor, employer, generatedDate));
        if (descriptors.length > 1) {
          // Yield between captures so the spinner can paint.
          await new Promise(r => setTimeout(r, 0));
        }
      }

      let filename;
      if (descriptors.length === 1) {
        const slug = kebabCase(sheets[0].label);
        filename = `stream-${slug}-${getEmployerSlug()}-${formatDateSlug(now)}.pdf`;
      } else {
        filename = `stream-analytics-${getEmployerSlug()}-${formatDateSlug(now)}.pdf`;
      }

      await generateSubsectionPdf(sheets, filename);
      return { success: true };
    } catch (err) {
      console.error('[PDF] Failed to generate subsection PDF:', err);
      return { success: false, error: err };
    } finally {
      isGenerating.value = false;
    }
  }

  async function downloadSheet(descriptor) {
    return downloadSheets([descriptor]);
  }

  return {
    isGenerating,
    downloadSheets,
    downloadSheet,
  };
}
